import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, ShieldCheck, Globe, Copy, Check, Trash2, Radio, ExternalLink } from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const UNCHANGED_KEY = "__UNCHANGED__";
const MASK_DISPLAY = "••••••••••••••••••••";

const TelegramConfig = ({ activeAiAgentId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [telegramConfig, setTelegramConfig] = useState({
    bot_token: "",
    is_active: false,
    streaming_enabled: false,
  });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const response = await ApiClient.request("/telegram/config", { aiAgentId: activeAiAgentId });
      if (response.success && response.config) {
        setTelegramConfig({
          bot_token: response.config.bot_token ? UNCHANGED_KEY : "",
          is_active: response.config.is_active || false,
          streaming_enabled: response.config.streaming_enabled || false,
        });
        setWebhookUrl(response.config.webhook_url || "");
      }
    } catch (error) {
      console.log("Telegram config not found");
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const saveConfig = async () => {
    if (!telegramConfig.bot_token.trim()) {
      showToast("Please enter your Bot Token", "error");
      return;
    }

    try {
      setSaving(true);
      const response = await ApiClient.request("/telegram/config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify(telegramConfig),
      });

      if (response.success) {
        setWebhookUrl(response.webhook_url);
        showToast("Telegram configuration saved successfully!", "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    } finally {
      setSaving(false);
    }
  };

  const deleteConfig = async () => {
    try {
      setSaving(true);
      const response = await ApiClient.request("/ai-agents/integrations/telegram", {
        method: "DELETE",
      });
      if (response.success) {
        setTelegramConfig({
          bot_token: "",
          is_active: false,
          streaming_enabled: false,
        });
        setWebhookUrl("");
        showToast("Telegram integration deleted successfully!", "success");
      }
    } catch (error) {
      // handled globally
    } finally {
      setSaving(false);
      setIsDeleteModalOpen(false);
    }
  };

  const getDisplayWebhookUrl = () => {
    if (!webhookUrl) return "";
    let path = webhookUrl;
    if (webhookUrl.startsWith("http://") || webhookUrl.startsWith("https://")) {
      try {
        const urlObj = new URL(webhookUrl);
        path = urlObj.pathname + urlObj.search;
      } catch (e) {}
    }
    const baseUrl = process.env.REACT_APP_API_BASE_URL || window.location.origin;
    let cleanBase = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    let cleanPath = path.startsWith("/") ? path : "/" + path;
    if (cleanBase.endsWith("/api") && cleanPath.startsWith("/api")) {
      cleanBase = cleanBase.slice(0, -4);
    }
    return cleanBase + cleanPath;
  };

  const displayUrl = getDisplayWebhookUrl();

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(displayUrl);
      setCopied(true);
      showToast("Webhook URL copied to clipboard!", "success");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      showToast("Failed to copy URL", "error");
    }
  };

  const toggleActive = async () => {
    if (!telegramConfig.is_active) {
      if (!telegramConfig.bot_token?.trim()) {
        showToast("Please enter and save your Bot Token first", "error");
        return;
      }
      if (telegramConfig.bot_token !== UNCHANGED_KEY) {
        showToast("Please save your configuration changes before activating", "warning");
        return;
      }
    }

    try {
      const newActiveState = !telegramConfig.is_active;
      const response = await ApiClient.request("/telegram/config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify({ is_active: newActiveState }),
      });

      if (response.success) {
        setTelegramConfig((prev) => ({ ...prev, is_active: response.active }));
        showToast(response.message, "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    }
  };

  const toggleStreaming = async () => {
    try {
      const newStreamingState = !telegramConfig.streaming_enabled;
      const response = await ApiClient.request("/telegram/config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify({ streaming_enabled: newStreamingState }),
      });

      if (response.success) {
        setTelegramConfig((prev) => ({ ...prev, streaming_enabled: newStreamingState }));
        showToast("Streaming status updated", "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-4 sm:space-y-6">
        
        {/* Top Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 sm:pb-5 border-b border-zinc-100 gap-3 sm:gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/integrations")}
              className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
              title="Back to Integrations"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Telegram Bot Integration</h1>
              <p className="text-xs text-zinc-500 mt-0.5">
                Connect your agent directly to Telegram messaging and group chats.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 self-start sm:self-auto pl-9 sm:pl-0">
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase border ${
              telegramConfig.is_active
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-zinc-50 text-zinc-500 border-zinc-200"
            }`}>
              {telegramConfig.is_active ? "Active" : "Disabled"}
            </span>
            <button
              type="button"
              onClick={toggleActive}
              className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 ${
                telegramConfig.is_active ? "bg-zinc-900" : "bg-zinc-200"
              }`}
              title={telegramConfig.is_active ? "Deactivate integration" : "Activate integration"}
            >
              <div
                className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform ${
                  telegramConfig.is_active ? "left-4" : "left-0.5"
                }`}
              />
            </button>
          </div>
        </div>

        {/* Bot Credentials Card */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-5">
          <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
            <ShieldCheck className="text-zinc-800 shrink-0" size={18} />
            <div>
              <h2 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Bot Credentials</h2>
              <p className="text-[11px] text-zinc-500">Provide the API token issued by Telegram's @BotFather</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-zinc-700 mb-1.5">
                Telegram Bot Token
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={telegramConfig.bot_token === UNCHANGED_KEY ? MASK_DISPLAY : telegramConfig.bot_token}
                  onChange={(e) =>
                    setTelegramConfig({
                      ...telegramConfig,
                      bot_token: e.target.value,
                    })
                  }
                  onFocus={() => {
                    if (telegramConfig.bot_token === UNCHANGED_KEY) {
                      setTelegramConfig({ ...telegramConfig, bot_token: "" });
                    }
                  }}
                  placeholder={telegramConfig.bot_token === UNCHANGED_KEY ? MASK_DISPLAY : "123456789:ABCdefGhIJKlmNoPQRsTUVwxyZ"}
                  className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono transition-colors"
                />
              </div>
              <p className="text-[11px] text-zinc-400 mt-1">
                Obtain this token by talking to{" "}
                <a
                  href="https://t.me/botfather"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-700 font-medium underline hover:text-zinc-900 inline-flex items-center gap-0.5"
                >
                  @BotFather <ExternalLink size={10} />
                </a>{" "}
                on Telegram and creating a new bot.
              </p>
            </div>

            {/* Streaming Toggle */}
            <div className="flex items-center justify-between p-3.5 bg-zinc-50 rounded-lg border border-zinc-200">
              <div className="space-y-0.5 pr-3">
                <div className="flex items-center gap-1.5">
                  <Radio size={13} className={telegramConfig.streaming_enabled ? "text-zinc-900" : "text-zinc-400"} />
                  <h4 className="text-xs font-medium text-zinc-900">Streaming Responses</h4>
                </div>
                <p className="text-[11px] text-zinc-500">
                  Stream response tokens as they are generated with animated typewriter updates.
                </p>
              </div>
              <button
                type="button"
                onClick={toggleStreaming}
                className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 ${
                  telegramConfig.streaming_enabled ? "bg-zinc-900" : "bg-zinc-200"
                }`}
                title={telegramConfig.streaming_enabled ? "Disable Streaming" : "Enable Streaming"}
              >
                <div
                  className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform ${
                    telegramConfig.streaming_enabled ? "left-4" : "left-0.5"
                  }`}
                />
              </button>
            </div>
          </div>

          <div className="pt-3 border-t border-zinc-100 flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-2.5">
            {telegramConfig.bot_token ? (
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={saving}
                className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-40"
              >
                <Trash2 size={14} />
                <span>Disconnect Telegram</span>
              </button>
            ) : <div />}

            <button
              type="button"
              onClick={saveConfig}
              disabled={saving || !telegramConfig.bot_token?.trim()}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-40 text-xs font-medium transition-colors"
            >
              {saving ? (
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Save size={14} />
              )}
              <span>{saving ? "Saving..." : "Save Configuration"}</span>
            </button>
          </div>
        </div>

        {/* Webhook Endpoint Card */}
        {webhookUrl && (
          <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-3">
            <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
              <Globe className="text-zinc-800 shrink-0" size={18} />
              <div>
                <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Registered Webhook URL</h3>
                <p className="text-[11px] text-zinc-500">Live webhook URL synced with Telegram's Bot API</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-1.5 sm:p-2 bg-zinc-50 rounded-lg border border-zinc-200">
              <div className="font-mono text-[11px] sm:text-xs text-zinc-700 px-2 py-1 flex-1 break-all select-all min-w-0">
                {displayUrl}
              </div>
              <button
                type="button"
                onClick={copyWebhookUrl}
                className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex-shrink-0 ${
                  copied
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-white text-zinc-800 border border-zinc-200 hover:bg-zinc-100"
                }`}
                title={copied ? "Copied!" : "Copy Webhook URL"}
              >
                {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                <span>{copied ? "Copied" : "Copy URL"}</span>
              </button>
            </div>

            <p className="text-[11px] text-zinc-400">
              This endpoint is automatically registered with Telegram via the <code className="text-zinc-600 bg-zinc-100 px-1 py-0.5 rounded text-[10px]">setWebhook</code> API when you save.
            </p>
          </div>
        )}
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deleteConfig}
        title="Disconnect Telegram Bot?"
        message="Are you sure you want to delete this integration? Your Telegram bot webhook will be unregistered and stopped."
        confirmText="Disconnect"
        isLoading={saving}
      />
    </div>
  );
};

export default TelegramConfig;
